import React, { FC, useCallback } from 'react';

import documentIcon from 'assets/images/svg/document.svg';
import downloadIcon from 'assets/images/svg/download.svg';
import { EyeIcon } from 'components/common/icons';
import { Image } from 'components/common/images';
import { useErrorCallback } from 'hooks';
import { cn } from 'utils';
import { downloadPDF } from 'utils/downloadPDF';

import styles from './DocumentCard.module.scss';

type Props = {
  title: string;
  documentUrl: string;
  date?: string;
  className?: string;
  downloadFileName?: string;
};

export const DocumentCard: FC<Props> = ({
  title,
  documentUrl,
  date,
  className,
  downloadFileName,
}) => {
  const handleError = useErrorCallback();

  const openDocument = useCallback(async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      await handleError(error instanceof Error ? error : new Error('Failed to open document'));
    }
  }, [documentUrl, handleError]);

  const downloadDocument = useCallback(async () => {
    await downloadPDF(documentUrl, downloadFileName ?? `${title}.pdf`, handleError);
  }, [documentUrl, downloadFileName, handleError, title]);

  return (
    <div className={cn(styles.card, className)}>
      <Image src={documentIcon} alt="" className={styles.icon} aria-hidden="true" />
      <div className={styles.content}>
        <div className={styles.title}>{title}</div>
        {date ? <div className={styles.date}>{date}</div> : null}
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => void downloadDocument()}
          aria-label={`Скачать ${title}`}
        >
          <Image src={downloadIcon} alt="" className={styles.downloadIcon} aria-hidden="true" />
        </button>
        <button
          type="button"
          className={styles.actionButton}
          onClick={() => void openDocument()}
          aria-label={`Открыть ${title}`}
        >
          <EyeIcon size={24} />
        </button>
      </div>
    </div>
  );
};
